import React, { useState, useContext } from 'react';
import { useRouteMatch } from 'react-router-dom';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { AuthContext } from '../auth/AuthContext';
import { Spinner } from '../Spinner';
import { storeToken } from '../tokenStorageUtil';
import { ErrorMessageDisplay } from '../ErrorMessageDisplay';

/**
 * Reusable component for Adobe Sign redirect disclaimer and flow
 *
 * @param {Object} props
 * @param {Function} props.createContract - Function to create the digital signature contract
 * @param {Function} props.getSigningUrl - Function to get the signing URL
 * @param {string} props.message - Message to display to the user
 */
const AdobeSignRedirectDisclaimer = (props) => {
    const { createContract, getSigningUrl, message } = props;

    const routeMatch = useRouteMatch();
    const { url } = routeMatch.params;

    const authContext = useContext(AuthContext);
    const [fetchingSigningUrl, setFetchingSigningUrl] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    // Handles errors during the digital signing process
    const handleSigningError = () => {
        // Reset the fetching flag and add the error message.
        setFetchingSigningUrl(false);
        setErrorMessage('Unable to connect to Adobe Sign. Please try again.');
    };

    const getDigitalSignatureSigningUrl = async () => {
        try {
            const res = await getSigningUrl(url, authContext);
            // On success, immediately redirect; spinner will naturally go away due to page unload.
            window.location.replace(res.data);
        } catch {
            // Stop the spinner & enable "Continue" button if there is an error so the user can retry.
            handleSigningError();
        }
    };

    const proceedToDigitallySign = async () => {
        setFetchingSigningUrl(true);
        setErrorMessage(null); // Clear any previous errors

        // store the token for facilitating the login-without-code post redirection after digitally signing
        const token = _.get(authContext, 'auth.token', null).split(' ')[1]; // "Bearer <token>"
        storeToken(token);

        try {
            await createContract(url, authContext);

            // setting a delay of 2 seconds to fetch the signing url
            // since it's not immediately created by the underlying Digital Signature provider and takes some time to
            // reflect. This works in dev without the delay since the API calls have an inherent lag.
            // On prod/non-prod instances, signing url is returned as null without the delay.
            setTimeout(getDigitalSignatureSigningUrl, 2000);
        } catch {
            // Stop the spinner & enable "Continue" button so the user can retry.
            handleSigningError();
        }
    };

    return (
        <div style={{ margin: 10, padding: 10 }}>
            <div>
                <p>{message}</p>
            </div>
            <div className="mt-4">
                <ErrorMessageDisplay errorMessage={errorMessage} size="sm" />
                <div className="h-1">
                    <Spinner showSpinner={fetchingSigningUrl} color={'blue'} />
                </div>
                <button
                    type="button"
                    className="disabled:opacity-50 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded float-right"
                    onClick={proceedToDigitallySign}
                    disabled={fetchingSigningUrl} // disable the button while fetching the signing url
                    data-testid="redirect-to-adobe-sign"
                >
                    Continue
                </button>
                <div className="clear-both" />
            </div>
        </div>
    );
};

AdobeSignRedirectDisclaimer.propTypes = {
    createContract: PropTypes.func.isRequired,
    getSigningUrl: PropTypes.func.isRequired,
    message: PropTypes.string.isRequired,
};

export default AdobeSignRedirectDisclaimer;
